import React, { useEffect, useState, useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { type MRT_ColumnDef } from "material-react-table";
import ReusableTable from "../../components/common/Table.tsx";
import Button from "../../components/common/Button.tsx";
import ChatBot from "../../components/common/chatBot/ChatBot.tsx";
import BreadCrumb from "../../components/common/BreadCrumb.tsx";
const PreviewDataList: React.FC = () => {
  const [datasetTableData, setDatasetTableData] = useState<any[]>([]);
  const [datasetTableHeaders, setDatasetTableHeaders] = useState<string[]>([]);
  const [previewTableData, setPreviewTableData] = useState<any[]>([]);
  const [previewTableHeaders, setPreviewTableHeaders] = useState<string[]>([]);
  const [tableLoading, setTableLoading] = useState<boolean>(true);
  const [tableError, setTableError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const storedData = sessionStorage.getItem("previewData");

    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);

        const formattedDataset = [
          {
            "Dataset Name": parsedData.dataset.dataset_name,
            "File Name": parsedData.dataset.file_name,
            "Total Records": parsedData.dataset.total_records,
            "Total Fields": parsedData.dataset.total_fields,
            "Created At": new Date(
              parsedData.dataset.created_at
            ).toLocaleString(),
            "Modified At": new Date(
              parsedData.dataset.modified_at
            ).toLocaleString(),
          },
        ];

        setDatasetTableHeaders(Object.keys(formattedDataset[0]));
        setDatasetTableData(formattedDataset);

        if (parsedData.preview.length > 0) {
          setPreviewTableHeaders(Object.keys(parsedData.preview[0]));
          setPreviewTableData(parsedData.preview);
        }

        setTableLoading(false);
      } catch (err) {
        console.error("Error parsing stored data:", err);
        setTableError("Failed to load preview data");
        setTableLoading(false);
      }
    } else {
      setTableError("No preview data available");
      setTableLoading(false);
    }
  }, []);

  const datasetColumns = useMemo<MRT_ColumnDef<any>[]>(() => {
    return datasetTableHeaders.map((header) => ({
      accessorKey: header,
      header: header
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      size: 150,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        if (typeof value === "boolean") {
          return value ? "True" : "False";
        }
        if (value === null || value === undefined) {
          return "-";
        }
        return value as React.ReactNode;
      },
    }));
  }, [datasetTableHeaders]);

  const previewColumns = useMemo<MRT_ColumnDef<any>[]>(() => {
    return previewTableHeaders.map((header) => ({
      accessorKey: header,
      header: header
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      size: 150,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        if (typeof value === "boolean") {
          return value ? "True" : "False";
        }
        if (value === null || value === undefined) {
          return "-";
        }
        return value as React.ReactNode;
      },
    }));
  }, [previewTableHeaders]);

  return (
    <>
    <BreadCrumb moduleName={"Dataset"} pageName={"View File"}/>
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Typography fontWeight="bold"> Dataset Preview</Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 2, borderRadius: 2, mb: 3 }}>
        <Typography fontWeight="bold" mb={2}>
          Uploaded File Details
        </Typography>

        <ReusableTable
          columns={datasetColumns}
          data={datasetTableData}
          loading={tableLoading}
          error={tableError}
          enablePagination={false}
          enableSorting={false}
          enableColumnFilters={false}
        />
      </Paper>

      <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
        <Typography fontWeight="bold" mb={2}>
          Data Preview (10 records)
        </Typography>

        <ReusableTable
          columns={previewColumns}
          data={previewTableData}
          loading={tableLoading}
          error={tableError}
          enablePagination={false}
          enableSorting={false}
          enableColumnFilters={false}
        />
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex", mt: 3, gap: 2 }}>
        <Button
          onClick={() => {
            sessionStorage.removeItem("previewData");
            navigate("/Datalist");
          }}
        >
          Close
        </Button>
      </Box>
      <ChatBot />
    </Box>
    </>
  );
};

export default PreviewDataList;
